import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Loader2 } from 'lucide-react';

export interface PullToRefreshProps {
  onRefresh: () => Promise<unknown> | unknown;
  children: ReactNode;
  /** Additional classes for the scroll container. */
  className?: string;
  /** Inline style for the scroll container. */
  style?: CSSProperties;
}

const TRIGGER_DISTANCE = 64;
const MAX_PULL = 120;

// Rubber-band: gradually resist further pulling past the trigger point.
function damp(distance: number): number {
  if (distance <= TRIGGER_DISTANCE) return distance;
  const extra = distance - TRIGGER_DISTANCE;
  return TRIGGER_DISTANCE + extra * 0.4;
}

export function PullToRefresh({
  onRefresh,
  children,
  className,
  style,
}: PullToRefreshProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number | null>(null);
  const trackingRef = useRef(false);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const fireRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
      setPull(0);
    }
  }, [onRefresh]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      if (refreshing) return;
      if (el.scrollTop > 0) {
        trackingRef.current = false;
        startYRef.current = null;
        return;
      }
      trackingRef.current = true;
      startYRef.current = e.touches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!trackingRef.current || refreshing || startYRef.current == null) return;
      // If the user has scrolled down since touchstart (shouldn't happen, but be safe),
      // abort tracking and let native scroll take over.
      if (el.scrollTop > 0) {
        trackingRef.current = false;
        setPull(0);
        return;
      }
      const dy = e.touches[0].clientY - startYRef.current;
      if (dy <= 0) {
        setPull(0);
        return;
      }
      // Prevent the page from actually scrolling/rubber-banding while we pull.
      if (e.cancelable) e.preventDefault();
      setPull(Math.min(damp(dy), MAX_PULL));
    };

    const onTouchEnd = () => {
      if (!trackingRef.current) return;
      trackingRef.current = false;
      startYRef.current = null;
      if (pull >= TRIGGER_DISTANCE) {
        void fireRefresh();
      } else {
        setPull(0);
      }
    };

    // passive: false on touchmove so preventDefault works.
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [fireRefresh, pull, refreshing]);

  const indicatorOffset = refreshing ? TRIGGER_DISTANCE : pull;
  const progress = Math.min(1, pull / TRIGGER_DISTANCE);
  const showIndicator = refreshing || pull > 0;

  return (
    <div
      ref={scrollRef}
      className={'relative overflow-y-auto' + (className ? ' ' + className : '')}
      style={{ overscrollBehavior: 'contain', ...style }}
    >
      <div
        aria-hidden={!showIndicator}
        className="pointer-events-none absolute left-0 right-0 flex justify-center"
        style={{
          top: 0,
          transform: `translateY(${indicatorOffset - 32}px)`,
          transition: refreshing || trackingRef.current ? 'none' : 'transform 200ms ease-out',
        }}
      >
        <div
          className="flex items-center justify-center w-8 h-8 rounded-full bg-background border border-border text-muted-foreground shadow-sm"
          style={{
            opacity: showIndicator ? 1 : 0,
            transition: 'opacity 150ms ease-out',
          }}
        >
          <Loader2
            size={16}
            className={refreshing ? 'animate-spin' : ''}
            style={refreshing ? undefined : { transform: `rotate(${progress * 270}deg)` }}
          />
        </div>
      </div>
      <div
        style={{
          transform: `translateY(${indicatorOffset}px)`,
          transition: refreshing || trackingRef.current ? 'none' : 'transform 200ms ease-out',
        }}
      >
        {children}
      </div>
    </div>
  );
}
